import BaseListPresenter from "../../../base/BaseListPresenter";
import { findObjectUseCase } from "../../../usecases/object";

class AuditLogsPresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }

  init() {
    this.limit = 20;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  async GenerateCSV() {
    const query = this.createQuery();
    query.limit = 0;
    const objects = await findObjectUseCase().execute(
      this.view.getCollectionName(),
      query
    );
    let csvContent = "DATE,USER,ACTIVITY,MESSAGE\n"; // CSV header

    objects.forEach((item) => {
      const logDate = this.wrapInQuotes(item.logDate);
      const user = this.wrapInQuotes(item.user);
      const activity = this.wrapInQuotes(item.activity);
      const message = this.wrapInQuotes(item.message);

      csvContent += `${logDate},${user},${activity},${message}\n`;
    });

    // Create a blob from CSV content
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    // Create a link element and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Audit Log Report-${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Helper function to wrap values in quotes if necessary
  wrapInQuotes(value) {
    // Convert to string and check if it contains a comma or new line
    const strValue = String(value);
    if (strValue.includes(",") || strValue.includes("\n")) {
      return `"${strValue.replace(/"/g, '""')}"`; // Wrap in quotes and escape any existing quotes
    }
    return strValue;
  }

  async GenerateExcel() {
    const query = this.createQuery();
    query.limit = 0;

    const objects = await findObjectUseCase().execute(
      this.view.getCollectionName(),
      query
    );

    // Define the basic XML structure for an Excel file
    let excelContent = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
            <meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8">
            <meta name="ProgId" content="Excel.Sheet">
            <meta name="Generator" content="Microsoft Excel 11">
        </head>
        <body>
            <table border="1">
                <tr>
                    <th>DATE</th>
                    <th>USER</th>
                    <th>ACTIVITY</th>
                    <th>MESSAGE</th>
                </tr>`;

    // Add rows dynamically
    objects.forEach((item) => {
      const logDate = this.escapeXml(item.logDate);
      const user = this.escapeXml(item.user);
      const activity = this.escapeXml(item.activity);
      const message = this.escapeXml(item.message);

      excelContent += `
                <tr>
                    <td>${logDate}</td>
                    <td>${user}</td>
                    <td>${activity}</td>
                    <td>${message}</td>
                </tr>`;
    });

    excelContent += `
            </table>
        </body>
        </html>`;

    // Create a blob from the Excel content
    const blob = new Blob([excelContent], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);

    // Create a link element and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Audit Log Report-${new Date().toISOString().split("T")[0]}.xls`
    );

    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // Helper function to escape XML special characters
  escapeXml(value) {
    if (!value) return "";
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");
  }
}
export default AuditLogsPresenter;
